<script lang="ts">
	import type { PageLink } from '$lib/docs/types';
	
	interface Props {
		previousPage?: PageLink;
		nextPage?: PageLink;
	}
	
	let { previousPage, nextPage }: Props = $props();
</script>

<nav class="page-nav" aria-label="Page navigation">
	<div class="page-nav__container">
		{#if previousPage}
			<a href="/docs/{previousPage.slug}" class="page-nav__link page-nav__link--prev">
				<div class="page-nav__direction">
					<span class="page-nav__arrow">←</span>
					<span class="page-nav__label">Previous</span>
				</div>
				<div class="page-nav__title">{previousPage.title}</div>
			</a>
		{:else}
			<div class="page-nav__placeholder"></div>
		{/if}
		
		{#if nextPage}
			<a href="/docs/{nextPage.slug}" class="page-nav__link page-nav__link--next">
				<div class="page-nav__direction">
					<span class="page-nav__label">Next</span>
					<span class="page-nav__arrow">→</span>
				</div>
				<div class="page-nav__title">{nextPage.title}</div>
			</a>
		{:else}
			<div class="page-nav__placeholder"></div>
		{/if}
	</div>
</nav>

<style>
	.page-nav {
		margin-top: 3rem;
		padding-top: 2rem;
		border-top: 1px solid var(--border-light);
	}
	
	.page-nav__container {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
	}
	
	.page-nav__link {
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
		padding: 1rem 1.25rem;
		background: transparent;
		border: 1px solid var(--border-light);
		border-radius: 6px;
		text-decoration: none;
		transition: all 0.15s ease;
		min-height: 4.5rem;
	}
	
	.page-nav__link:hover {
		background: var(--bg-secondary);
		border-color: var(--border-default);
	}
	
	.page-nav__link--prev {
		justify-self: start;
		align-items: flex-start;
		text-align: left;
	}
	
	.page-nav__link--next {
		justify-self: end;
		align-items: flex-end;
		text-align: right;
	}
	
	.page-nav__direction {
		display: flex;
		align-items: center;
		gap: 0.375rem;
		font-size: 0.75rem;
		color: var(--text-tertiary);
		font-weight: 500;
	}
	
	.page-nav__link--next .page-nav__direction {
		flex-direction: row-reverse;
	}
	
	.page-nav__arrow {
		color: var(--text-tertiary);
		font-size: 0.875rem;
		transition: transform 0.15s ease;
	}
	
	.page-nav__link:hover .page-nav__arrow {
		transform: translateX(2px);
	}
	
	.page-nav__link--prev:hover .page-nav__arrow {
		transform: translateX(-2px);
	}
	
	.page-nav__label {
		text-transform: uppercase;
		letter-spacing: 0.05em;
		font-size: 0.75rem;
	}
	
	.page-nav__title {
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--text-primary);
		line-height: 1.4;
	}
	
	.page-nav__placeholder {
		/* Empty space when no previous/next page */
	}
	
	/* Dark mode adjustments */
	:global([data-theme='dark']) .page-nav {
		border-top-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .page-nav__link {
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .page-nav__link:hover {
		background: rgba(255, 255, 255, 0.02);
		border-color: rgba(255, 255, 255, 0.12);
	}
	
	:global([data-theme='dark']) .page-nav__direction {
		color: rgba(255, 255, 255, 0.5);
	}
	
	:global([data-theme='dark']) .page-nav__arrow {
		color: rgba(255, 255, 255, 0.5);
	}
	
	:global([data-theme='dark']) .page-nav__title {
		color: rgba(255, 255, 255, 0.9);
	}
	
	@media (max-width: 768px) {
		.page-nav__container {
			grid-template-columns: 1fr;
			gap: 0.75rem;
		}
		
		.page-nav__link {
			padding: 1rem;
			text-align: left;
			align-items: flex-start;
		}
		
		.page-nav__link--next {
			text-align: left;
			align-items: flex-start;
		}
		
		.page-nav__link--next .page-nav__direction {
			flex-direction: row;
		}
		
		.page-nav__title {
			font-size: 0.875rem;
		}
	}
</style>